import ErrorLevel from "./ErrorLevel";

/**
 * Категории breadcrumb
 * Используется для группировки и фильтрации в Sentry UI
 */
export enum BreadcrumbCategory {
  Navigation = 'navigation',
  Http = 'http',
  UI = 'ui',
  Console = 'console',
  User = 'user',
  Custom = 'custom',
  RPC = 'rpc',
  Debug = 'debug',
  Server = 'server'
}

/**
 * Breadcrumb - хлебная крошка для отслеживания действий пользователя
 *
 * Крошка хранит СНИМОК данных, а не ссылки на них: create приводит data
 * к простым значениям сразу, в момент создания. Инвариант вшит в фабрику
 * по тем же соображениям, что округление до SCALE вшито в конструктор
 * Numeric — снаружи испортить нельзя, помнить об этом не нужно.
 *
 * Зачем, если JSON.stringify конверта и так позовёт toJSON:
 *
 * 1. СНИМОК. Крошка лежит в буфере до следующей ошибки. Ссылка на модель
 *    к моменту отправки покажет УЖЕ ПЕРЕСЧИТАННЫЕ значения, то есть
 *    крошка соврёт про то, с чем шёл расчёт.
 *
 * 2. ЖИВУЧЕСТЬ. Циклическая ссылка (сущность → родитель → сущность) роняет
 *    JSON.stringify в SentryService.#buildEnvelope, и теряется ВСЁ событие,
 *    а не одна крошка. Здесь цикл становится "[circular]". То же с bigint —
 *    на нём JSON.stringify кидает TypeError.
 *
 * 3. ОБЪЁМ. 50 крошек, каждая со ссылкой на граф сущностей, — это и память
 *    в рантайме, и мегабайтный конверт. Глубина и длина режутся здесь.
 *
 * 4. ЧИТАЕМОСТЬ. ref без обработки уезжает как {_rawValue,_value,dep},
 *    reactive-прокси тянет за собой __v_ поля.
 */
class SentryBreadcrumb {

  timestamp: number;
  category: BreadcrumbCategory;
  message: string;
  level: ErrorLevel;
  data: Record<string, any>;

  /**
   * Глубже не идём: крошка — не место для графа сущностей.
   *
   * Ниже 4 опускать нельзя: Numeric и TimeStamp — объекты, и обрезка бьёт
   * по ним самим. В типичном вызове trace('текст', { input: {...} }) данные
   * лежат на 0, payload на 1, input на 2, а цена уже на 3 — при MAX_DEPTH = 3
   * вместо цены будет [depth limit]. 4 показывает значения, 5 — ещё и поля
   * сущности внутри них.
   */
  private static readonly MAX_DEPTH = 5;

  /** Длинные массивы режем: список позиций в каждой из 50 крошек не нужен. */
  private static readonly MAX_ITEMS = 50;

  /** Строки режем: сюда иногда прилетает html или base64. */
  private static readonly MAX_STRING = 2000;

  private constructor() {
    this.timestamp = Date.now() / 1000;
    this.category = BreadcrumbCategory.Custom;
    this.message = '';
    this.level = ErrorLevel.Info;
    this.data = {};
  }

  static create(
    message: string,
    category: BreadcrumbCategory,
    level: ErrorLevel,
    data: Record<string, any> = {}
  ): SentryBreadcrumb {
    const crumb = new SentryBreadcrumb();
    crumb.message = message;
    crumb.category = category;
    crumb.level = level;
    crumb.data = this.snapshot(data);
    return crumb;
  }

  /**
   * Создание из серверной крошки (RpcDebug::addBreadcrumb)
   * Сохраняет оригинальный timestamp с бэкенда
   */
  static fromServer(serverCrumb: { timestamp: number; message: string; data?: any }): SentryBreadcrumb {
    const crumb = new SentryBreadcrumb();
    crumb.timestamp = serverCrumb.timestamp;
    crumb.message = serverCrumb.message;
    crumb.category = BreadcrumbCategory.Server;
    crumb.level = ErrorLevel.Info;
    /** Снимок не нужен: с бека приходит уже разобранный JSON —
     *  ни живых ссылок, ни циклов, ни Numeric в нём быть не может. */
    crumb.data = serverCrumb.data ?? {};
    return crumb;
  }

  static fromNavigation(to: any, from: any): SentryBreadcrumb {
    return SentryBreadcrumb.create(
      `${from?.path || 'initial'} → ${to.path}`,
      BreadcrumbCategory.Navigation,
      ErrorLevel.Info,
      { from: from?.path, to: to.path, toName: to.name, fromName: from?.name }
    );
  }

  /**
   * id — идентификатор сущности, с которой открыт диалог (props.id).
   * Если диалог открыт без id (например, режим создания), undefined
   * выпадает при JSON.stringify конверта и в Sentry не попадает.
   */
  static fromDialog(dialogName: string, action: 'open' | 'close', id?: number | string): SentryBreadcrumb {
    return SentryBreadcrumb.create(
      `Dialog ${action}: ${dialogName}`,
      BreadcrumbCategory.UI,
      ErrorLevel.Info,
      { id }
    );
  }

  //////////////////////////////////////

  /**
   * Снять снимок данных крошки: значения фиксируются здесь и сейчас,
   * дальше объект неизменяем и безопасен для JSON.stringify.
   */
  private static snapshot(data: Record<string, any>): Record<string, any> {
    return this.convert(data, 0, new WeakSet<object>());
  }

  /**
   * Диспетчер по типам. Доменных типов здесь нет намеренно: и Numeric,
   * и Decimal, и всё из types/ отдают себя через toJSON — его зовёт
   * convertObject. Логгеру незачем знать про арифметику.
   *
   * Ни одна ветка не приводит значение к number неявно: у Numeric
   * valueOf() кидает, и такое приведение взорвалось бы прямо здесь.
   */
  private static convert(value: any, depth: number, seen: WeakSet<object>): any {
    if (value === null || value === undefined) {
      return value;
    }

    // Снимаем ref и reactive-прокси: дальше работаем с сырым объектом.
    let plain = value;
    if (isRef(plain)) {
      plain = plain.value;
    }
    plain = toRaw(plain);

    if (plain instanceof Error) {
      return { name: plain.name, message: plain.message };
    } else if (typeof plain === 'bigint') {
      return plain.toString();
    } else if (typeof plain === 'symbol') {
      return plain.toString();
    } else if (typeof plain === 'function') {
      return `[function ${plain.name}]`;
    } else if (typeof plain === 'string') {
      if (plain.length > this.MAX_STRING) {
        return `${plain.slice(0, this.MAX_STRING)}… (обрезано, всего ${plain.length})`;
      } else {
        return plain;
      }
    } else if (typeof plain !== 'object') {
      // number, boolean — уходят как есть
      return plain;
    } else if (seen.has(plain)) {
      return '[circular]';
    } else if (depth >= this.MAX_DEPTH) {
      return '[depth limit]';
    } else {
      seen.add(plain);
      return this.convertObject(plain, depth, seen);
    }
  }

  /**
   * Объекты и массивы. Если у объекта есть свой toJSON (TimeStamp,
   * Serializable-сущности с @NoSerialize) — доверяем ему и нормализуем
   * уже результат, иначе обходим собственные поля.
   */
  private static convertObject(plain: any, depth: number, seen: WeakSet<object>): any {
    if (Array.isArray(plain)) {
      const items = plain.slice(0, this.MAX_ITEMS).map((item) => {
        return this.convert(item, depth + 1, seen);
      });

      if (plain.length > this.MAX_ITEMS) {
        items.push(`… (обрезано, всего ${plain.length})`);
      }

      return items;
    }

    /** toJSON зовём БЕЗ аргументов. JSON.stringify передал бы туда имя ключа,
     *  а у Serializable первый параметр — fieldsToSimplify, и строку он
     *  оборачивает в массив: поле, совпавшее по имени с ключом, молча
     *  упростилось бы. Здесь этого не происходит. */
    if (typeof plain.toJSON === 'function') {
      try {
        return this.convert(plain.toJSON(), depth + 1, seen);
      } catch (e) {
        return `[toJSON failed: ${(e as Error).message}]`;
      }
    }

    const result: Record<string, any> = {};

    for (const [key, item] of Object.entries(plain)) {
      // Служебные поля Vue в крошке только мешают
      if (key.startsWith('__v_')) {
        continue;
      }

      result[key] = this.convert(item, depth + 1, seen);
    }

    return result;
  }

  //////////////////////////////////////

  toPayload(): Record<string, any> {
    return {
      timestamp: this.timestamp,
      category: this.category,
      message: this.message,
      level: this.level,
      data: this.data
    };
  }
}

export default SentryBreadcrumb;